// ─────────────────────────────────────────────────────────────
// Page Instruction Types
// Types for contextual user guidance system
// ─────────────────────────────────────────────────────────────

/**
 * Page instruction content DTO from API.
 */
export interface PageInstructionDto {
  pageInstructionId: string;
  programId: string | null;
  pageRouteKey: string;
  roleId: string | null;
  roleName: string | null;
  contextKey: string | null;
  title: string;
  purpose: string;
  keyActions: string[];
  requiredSteps: string[];
  additionalNotes: string | null;
  isActive: boolean;
  displayOrder: number;
}

/**
 * User's instruction display preference.
 */
export interface UserInstructionPreferenceDto {
  preferenceId: string | null;
  pageRouteKey: string;
  programId: string | null;
  isCollapsed: boolean;
  dontShowAgain: boolean;
  isFirstVisit: boolean;
  viewCount: number;
}

/**
 * Combined instruction with user preference response.
 */
export interface PageInstructionWithPreferenceDto {
  instruction: PageInstructionDto | null;
  preference: UserInstructionPreferenceDto;
  hasInstruction: boolean;
}

export interface PageInstructionEngineItemDto extends PageInstructionDto {
  resolutionScope: string;
  contextMatched: boolean;
  resolutionPriority: number;
}

export interface PageInstructionEngineResponseDto {
  instructions: PageInstructionEngineItemDto[];
  preference: UserInstructionPreferenceDto;
  hasInstruction: boolean;
  primaryInstruction: PageInstructionEngineItemDto | null;
}

/**
 * Request to update user preference.
 */
export interface UpdateInstructionPreferenceRequest {
  pageRouteKey: string;
  programId?: string | null;
  isCollapsed?: boolean;
  dontShowAgain?: boolean;
}

export interface GetInstructionEngineRequest {
  pageRouteKey: string;
  programId?: string | null;
  roleId?: string | null;
  activeContextKeys?: string[];
}

export interface CreatePageInstructionRequest {
  programId?: string | null;
  pageRouteKey: string;
  roleId?: string | null;
  contextKey?: string | null;
  title: string;
  purpose: string;
  keyActions: string[];
  requiredSteps: string[];
  additionalNotes?: string | null;
  isActive: boolean;
  displayOrder: number;
}

export interface UpdatePageInstructionRequest extends CreatePageInstructionRequest {}

export interface PageInstructionListItemDto {
  pageInstructionId: string;
  programId: string | null;
  programName: string | null;
  pageRouteKey: string;
  roleId: string | null;
  roleName: string | null;
  contextKey: string | null;
  title: string;
  isActive: boolean;
  displayOrder: number;
  createdAt: string;
  updatedAt: string | null;
}

/**
 * Page route key constants matching backend.
 */
export const PAGE_ROUTE_KEYS = {
  PROGRAM_LIST: 'program-list',
  GLOBAL_USER_LIST: 'global-user-list',
  ERROR_LOGS: 'error-logs',
  PROGRAM_ROLE_LIST: 'program-role-list',
  WORKSPACE: 'workspace',
  SCOPED_DASHBOARD: 'scoped-dashboard',
  PATIENT_WORKSPACE: 'patient-workspace',
  PATIENT_360: 'patient-360',
  PATIENT_CYCLE_TIMELINE: 'patient-cycle-timeline',
  HOSPITAL_CREATE: 'hospital-create',
  HOSPITAL_EDIT: 'hospital-edit',
  HCP_CREATE: 'hcp-create',
  HCP_EDIT: 'hcp-edit',
  PATIENT_CREATE: 'patient-create',
  PATIENT_EDIT: 'patient-edit',
  ASSISTANCE_CASE_CREATE: 'assistance-case-create',
  ASSISTANCE_CASE_EDIT: 'assistance-case-edit',
  ASSISTANCE_CASE_LIST: 'assistance-case-list',
  WORKFLOW_SETUP: 'workflow-setup',
  WORKFLOW_DEBUG: 'workflow-debug',
  ROLE_MAPPING: 'role-mapping',
  FORM_BUILDER: 'form-builder',
  PROGRAM_FORMS: 'program-forms',
  AUDIT_LOGS: 'audit-logs',
  COMMUNICATION_TEMPLATES: 'communication-templates',
  AUTOMATION_MONITOR: 'automation-monitor',
  PROGRAM_DASHBOARD: 'program-dashboard',
  OPERATIONAL_DASHBOARD: 'operational-dashboard',
  PROGRAM_ADMIN_DASHBOARD: 'program-admin-dashboard',
  PROGRAM_UI_SETTINGS: 'program-ui-settings',
  SELF_REGISTRATION_SETTINGS: 'self-registration-settings',
  ADMIN_SETTINGS: 'admin-settings',
  INSTRUCTION_MANAGER: 'instruction-manager',
  FINANCIAL_PLANS: 'financial-plans',
  USER_ASSIGNMENT: 'user-assignment',
  ELIGIBILITY_RULESETS: 'eligibility-rulesets',
  CALL_LOG_LIST: 'call-log-list',
  CALL_LOG_ANALYTICS: 'call-log-analytics',
  CALL_LOG_FOLLOWUP_WORKLIST: 'call-log-followup-worklist',
  CALL_LOG_FORM: 'call-log-form',
  SLA_ESCALATIONS: 'sla-escalations',
  PROGRAM_REPORTS: 'program-reports',
  EMAIL_TEMPLATES: 'email-templates',
  LEGAL_PAGES_MANAGEMENT: 'legal-pages-management',
  NOTIFICATION_ENGINE: 'notification-engine',
  PHARMA_WORKSPACE: 'pharma-workspace',
  ACTIONABLE_INSIGHTS: 'actionable-insights',
  WORKFLOW_AUTO_HEALING: 'workflow-auto-healing',
  WORKFLOW_ADAPTIVE_RULES: 'workflow-adaptive-rules',
  WORKFLOW_TEMPLATES: 'workflow-templates',
  PROGRAM_DEMO_GUIDE: 'program-demo-guide',
  AUTOMATION_RULES: 'automation-rules',
  AUTOMATION_RULE_DETAIL: 'automation-rule-detail',
  AUTOMATION_RULE_REVIEW_QUEUE: 'automation-rule-review-queue',
  ENTITY_CODE_SETTINGS: 'entity-code-settings',
  ENTITY_FORM_LIST: 'entity-form-list',
  PHARMACY_CREATE: 'pharmacy-create',
  PHARMACY_EDIT: 'pharmacy-edit',
  DISPENSE_CASE_CREATE: 'dispense-case-create',
  DISPENSE_CASE_EDIT: 'dispense-case-edit',
  HOLIDAY_CALENDARS: 'holiday-calendars',
  BUSINESS_HOURS: 'business-hours',
  PROGRAM_LOCATION_SETTINGS: 'program-location-settings',
  ENTITY_INHERITANCE: 'entity-inheritance',
  DOCUMENT_REQUIREMENTS: 'document-requirements',
  PROGRAM_GOVERNANCE: 'program-governance',
  DASHBOARD_GOVERNANCE: 'dashboard-governance',
  PROGRAM_NOTIFICATIONS: 'program-notifications',
  NOTIFICATIONS_INBOX: 'notifications-inbox',
  PROGRAM_COMMUNICATIONS: 'program-communications',
  HOSPITAL_DASHBOARD: 'hospital-dashboard',
  HOSPITAL_OVERVIEW: 'hospital-overview',
  HOSPITAL_OPERATIONS: 'hospital-operations',
  HOSPITAL_INTELLIGENCE: 'hospital-intelligence',
  HOSPITAL_HCPS: 'hospital-hcps',
  HOSPITAL_PATIENTS: 'hospital-patients',
  HOSPITAL_CASES: 'hospital-cases',
  HOSPITAL_ASSISTANCE: 'hospital-assistance',
  HOSPITAL_ANALYTICS: 'hospital-analytics',
  HOSPITAL_PACKAGE_REQUESTS: 'hospital-package-requests',
  HCP_OVERVIEW: 'hcp-overview',
  HCP_OPERATIONS: 'hcp-operations',
  HCP_PATIENT_APPROVALS: 'hcp-patient-approvals',
  PATIENT_REGISTRATION_APPROVALS: 'patient-registration-approvals',
  PATIENT_REGISTRATION_APPROVAL_REVIEW: 'patient-registration-approval-review',
  HCP_NOTIFICATIONS: 'hcp-notifications',
  HCP_INTELLIGENCE: 'hcp-intelligence',
  PROGRAM_REQUESTS: 'program-requests',
  PATIENT_CYCLE_REQUEST: 'patient-cycle-request',
  CYCLE_REQUEST_QUEUE: 'cycle-request-queue',
} as const;

export type PageRouteKey = typeof PAGE_ROUTE_KEYS[keyof typeof PAGE_ROUTE_KEYS];

/**
 * Display names for page route keys.
 */
export const PAGE_ROUTE_KEY_DISPLAY_NAMES: Record<PageRouteKey, string> = {
  [PAGE_ROUTE_KEYS.PROGRAM_LIST]: 'Program List',
  [PAGE_ROUTE_KEYS.GLOBAL_USER_LIST]: 'Global User List',
  [PAGE_ROUTE_KEYS.ERROR_LOGS]: 'Error Logs',
  [PAGE_ROUTE_KEYS.PROGRAM_ROLE_LIST]: 'Program Role List',
  [PAGE_ROUTE_KEYS.WORKSPACE]: 'Workspace',
  [PAGE_ROUTE_KEYS.SCOPED_DASHBOARD]: 'Scoped Dashboard',
  [PAGE_ROUTE_KEYS.PATIENT_WORKSPACE]: 'Patient Workspace',
  [PAGE_ROUTE_KEYS.PATIENT_360]: 'Patient Details',
  [PAGE_ROUTE_KEYS.PATIENT_CYCLE_TIMELINE]: 'Patient Cycle Timeline',
  [PAGE_ROUTE_KEYS.HOSPITAL_CREATE]: 'Hospital Create',
  [PAGE_ROUTE_KEYS.HOSPITAL_EDIT]: 'Hospital Edit',
  [PAGE_ROUTE_KEYS.HCP_CREATE]: 'HCP Create',
  [PAGE_ROUTE_KEYS.HCP_EDIT]: 'HCP Edit',
  [PAGE_ROUTE_KEYS.PATIENT_CREATE]: 'Patient Create',
  [PAGE_ROUTE_KEYS.PATIENT_EDIT]: 'Patient Edit',
  [PAGE_ROUTE_KEYS.ASSISTANCE_CASE_CREATE]: 'Assistance Case Create',
  [PAGE_ROUTE_KEYS.ASSISTANCE_CASE_EDIT]: 'Assistance Case Edit',
  [PAGE_ROUTE_KEYS.ASSISTANCE_CASE_LIST]: 'Assistance Case List',
  [PAGE_ROUTE_KEYS.WORKFLOW_SETUP]: 'Workflow Configuration',
  [PAGE_ROUTE_KEYS.WORKFLOW_DEBUG]: 'Workflow Visual Debugging',
  [PAGE_ROUTE_KEYS.ROLE_MAPPING]: 'Role & Permission Mapping',
  [PAGE_ROUTE_KEYS.FORM_BUILDER]: 'Form Builder',
  [PAGE_ROUTE_KEYS.PROGRAM_FORMS]: 'Program Forms',
  [PAGE_ROUTE_KEYS.AUDIT_LOGS]: 'Audit History',
  [PAGE_ROUTE_KEYS.PROGRAM_DASHBOARD]: 'Program Dashboard',
  [PAGE_ROUTE_KEYS.OPERATIONAL_DASHBOARD]: 'Operational Dashboard',
  [PAGE_ROUTE_KEYS.PROGRAM_ADMIN_DASHBOARD]: 'Admin Dashboard',
  [PAGE_ROUTE_KEYS.PROGRAM_UI_SETTINGS]: 'Program UI Settings',
  [PAGE_ROUTE_KEYS.SELF_REGISTRATION_SETTINGS]: 'Self-Registration Settings',
  [PAGE_ROUTE_KEYS.ADMIN_SETTINGS]: 'Application Settings',
  [PAGE_ROUTE_KEYS.INSTRUCTION_MANAGER]: 'Instruction Manager',
  [PAGE_ROUTE_KEYS.FINANCIAL_PLANS]: 'Financial Assistance Plans',
  [PAGE_ROUTE_KEYS.USER_ASSIGNMENT]: 'User & Role Assignment',
  [PAGE_ROUTE_KEYS.ELIGIBILITY_RULESETS]: 'Eligibility Rulesets',
  [PAGE_ROUTE_KEYS.CALL_LOG_LIST]: 'Call Logs',
  [PAGE_ROUTE_KEYS.CALL_LOG_ANALYTICS]: 'Call Log Analytics',
  [PAGE_ROUTE_KEYS.CALL_LOG_FOLLOWUP_WORKLIST]: 'Follow-up Worklist',
  [PAGE_ROUTE_KEYS.CALL_LOG_FORM]: 'Call Log Form',
  [PAGE_ROUTE_KEYS.SLA_ESCALATIONS]: 'SLA Escalations',
  [PAGE_ROUTE_KEYS.PROGRAM_REPORTS]: 'Reports & Analytics',
  [PAGE_ROUTE_KEYS.EMAIL_TEMPLATES]: 'Email Templates',
  [PAGE_ROUTE_KEYS.LEGAL_PAGES_MANAGEMENT]: 'Legal Pages Management',
  [PAGE_ROUTE_KEYS.NOTIFICATION_ENGINE]: 'Notification Engine',
  [PAGE_ROUTE_KEYS.PHARMA_WORKSPACE]: 'Pharma Workspace',
  [PAGE_ROUTE_KEYS.ACTIONABLE_INSIGHTS]: 'Actionable Insights',
  [PAGE_ROUTE_KEYS.WORKFLOW_AUTO_HEALING]: 'Workflow Auto-Healing',
  [PAGE_ROUTE_KEYS.WORKFLOW_ADAPTIVE_RULES]: 'Adaptive Workflow Rules',
  [PAGE_ROUTE_KEYS.WORKFLOW_TEMPLATES]: 'Workflow Template Library',
  [PAGE_ROUTE_KEYS.PROGRAM_DEMO_GUIDE]: 'Program Demo Guide',
  [PAGE_ROUTE_KEYS.AUTOMATION_RULES]: 'Automation Rules',
  [PAGE_ROUTE_KEYS.AUTOMATION_RULE_DETAIL]: 'Automation Rule Detail',
  [PAGE_ROUTE_KEYS.AUTOMATION_RULE_REVIEW_QUEUE]: 'Automation Rule Review Queue',
  [PAGE_ROUTE_KEYS.ENTITY_CODE_SETTINGS]: 'Entity Code Settings',
  [PAGE_ROUTE_KEYS.ENTITY_FORM_LIST]: 'Entity Form List',
  [PAGE_ROUTE_KEYS.PHARMACY_CREATE]: 'Pharmacy Create',
  [PAGE_ROUTE_KEYS.PHARMACY_EDIT]: 'Pharmacy Edit',
  [PAGE_ROUTE_KEYS.DISPENSE_CASE_CREATE]: 'Dispense Case Create',
  [PAGE_ROUTE_KEYS.DISPENSE_CASE_EDIT]: 'Dispense Case Edit',
  [PAGE_ROUTE_KEYS.COMMUNICATION_TEMPLATES]: 'Communication Templates',
  [PAGE_ROUTE_KEYS.AUTOMATION_MONITOR]: 'Automation Monitor',
  [PAGE_ROUTE_KEYS.HOLIDAY_CALENDARS]: 'Holiday Calendars',
  [PAGE_ROUTE_KEYS.BUSINESS_HOURS]: 'Business Hours',
  [PAGE_ROUTE_KEYS.PROGRAM_LOCATION_SETTINGS]: 'Location Settings',
  [PAGE_ROUTE_KEYS.ENTITY_INHERITANCE]: 'Entity Inheritance Mapping',
  [PAGE_ROUTE_KEYS.DOCUMENT_REQUIREMENTS]: 'Document Requirements',
  [PAGE_ROUTE_KEYS.PROGRAM_GOVERNANCE]: 'Program Governance',
  [PAGE_ROUTE_KEYS.DASHBOARD_GOVERNANCE]: 'Dashboard Governance',
  [PAGE_ROUTE_KEYS.PROGRAM_NOTIFICATIONS]: 'Program Notifications',
  [PAGE_ROUTE_KEYS.NOTIFICATIONS_INBOX]: 'Notification Center',
  [PAGE_ROUTE_KEYS.PROGRAM_COMMUNICATIONS]: 'Program Communications',
  [PAGE_ROUTE_KEYS.HOSPITAL_DASHBOARD]: 'Hospital Dashboard',
  [PAGE_ROUTE_KEYS.HOSPITAL_OVERVIEW]: 'Hospital Overview',
  [PAGE_ROUTE_KEYS.HOSPITAL_OPERATIONS]: 'Hospital Operations',
  [PAGE_ROUTE_KEYS.HOSPITAL_INTELLIGENCE]: 'Hospital Intelligence',
  [PAGE_ROUTE_KEYS.HOSPITAL_HCPS]: 'Hospital HCPs',
  [PAGE_ROUTE_KEYS.HOSPITAL_PATIENTS]: 'Hospital Patients',
  [PAGE_ROUTE_KEYS.HOSPITAL_CASES]: 'Hospital Cases',
  [PAGE_ROUTE_KEYS.HOSPITAL_ASSISTANCE]: 'Hospital Assistance',
  [PAGE_ROUTE_KEYS.HOSPITAL_ANALYTICS]: 'Hospital Analytics',
  [PAGE_ROUTE_KEYS.HOSPITAL_PACKAGE_REQUESTS]: 'Hospital Package Requests',
  [PAGE_ROUTE_KEYS.HCP_OVERVIEW]: 'HCP Overview',
  [PAGE_ROUTE_KEYS.HCP_OPERATIONS]: 'HCP Operations',
  [PAGE_ROUTE_KEYS.HCP_PATIENT_APPROVALS]: 'Patient Approvals',
  [PAGE_ROUTE_KEYS.PATIENT_REGISTRATION_APPROVALS]: 'Patient Registration Approvals',
  [PAGE_ROUTE_KEYS.PATIENT_REGISTRATION_APPROVAL_REVIEW]: 'Patient Registration Approval Review',
  [PAGE_ROUTE_KEYS.HCP_NOTIFICATIONS]: 'HCP Notifications',
  [PAGE_ROUTE_KEYS.HCP_INTELLIGENCE]: 'HCP Intelligence',
  [PAGE_ROUTE_KEYS.PROGRAM_REQUESTS]: 'Program Requests',
  [PAGE_ROUTE_KEYS.PATIENT_CYCLE_REQUEST]: 'Request Treatment Cycle',
  [PAGE_ROUTE_KEYS.CYCLE_REQUEST_QUEUE]: 'Cycle Request Queue',
};
